import { Badge, createDataTableColumnHelper, DataTable, SearchField } from "@beweegbaas/ui";
import { createFileRoute, stripSearchParams, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { toast } from "@beweegbaas/ui";
import { DataTableActions, type DataTableAction } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { FilterBar, type FilterBarFilter } from "#/components/filter-bar";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import { USERS_ADMIN_SEARCH_DEFAULTS, usersAdminSearchSchema } from "#/modules/users/schema.search";
import {
  getAdminUsersOverview,
  resetUser2FA,
  toggleUserBlock,
} from "#/modules/users/users.functions";
import { startImpersonation } from "#/modules/util.impersonate/impersonate.functions";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin/users/")({
  validateSearch: usersAdminSearchSchema,
  // Keeps default filter/sort/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(USERS_ADMIN_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the loader/server-fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({
    searchTerm: search.searchTerm,
    orderBy: search.orderBy,
    orderDirection: search.orderDirection,
    page: search.page,
    pageSize: search.pageSize,
    has2FaEnabled: search.has2FaEnabled,
  }),
  loader: ({ deps }) => getAdminUsersOverview({ data: deps }),
  component: UsersAdminOverview,
  errorComponent: UsersError,
});

function UsersError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

const MS_PER_DAY = 86_400_000;

function lastActivityRecencyVariant(
  lastActivityAt: string | null | undefined,
): "green" | "orange" | "red" {
  if (!lastActivityAt) return "red";
  const last = new Date(lastActivityAt).getTime();
  if (Number.isNaN(last)) return "red";
  const daysAgo = Math.floor((Date.now() - last) / MS_PER_DAY);
  if (daysAgo <= 7) return "green";
  if (daysAgo <= 14) return "orange";
  return "red";
}

type AdminUser = Awaited<ReturnType<typeof getAdminUsersOverview>>["users"][number];

function UserRowActions({ user }: { user: AdminUser }) {
  const router = useRouter();
  const { locale } = Route.useParams();
  const impersonate = useServerFn(startImpersonation);
  const toggleBlock = useServerFn(toggleUserBlock);
  const reset2FA = useServerFn(resetUser2FA);

  async function handleToggleBlock() {
    const shouldBlock = !user.isBlocked;
    try {
      await toggleBlock({ data: { targetUserId: user.userId, shouldBlock } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    toast.success(shouldBlock ? "Gebruiker geblokkeerd." : "Gebruiker gedeblokkeerd.");
    await router.invalidate();
  }

  const actions: DataTableAction[] = [
    {
      key: "impersonate",
      label: `Impersonate ${user.name}`,
      icon: <IconMaterialSymbolsPerson />,
      onClick: () => {
        void impersonate({ data: { targetUserId: user.userId, locale } });
      },
    },
    ...(user.canBeBlocked || user.isBlocked
      ? [
          {
            key: "toggle-block",
            label: user.isBlocked ? `Deblokkeer ${user.name}` : `Blokkeer ${user.name}`,
            icon: user.isBlocked ? <IconMaterialSymbolsLockOpen /> : <IconMaterialSymbolsBlock />,
            onClick: () => void handleToggleBlock(),
          },
        ]
      : []),
    ...(user.canReset2Fa
      ? [
          {
            key: "reset-2fa",
            label: m["admin.users.table.reset_2fa"]({ name: user.name }),
            icon: <IconMaterialSymbolsLockReset />,
            destructive: true,
            confirm: {
              title: m["admin.users.table.reset_2fa_confirm_title"](),
              message: m["admin.users.table.reset_2fa_confirm_message"]({ name: user.name }),
            },
            onClick: async () => {
              await reset2FA({ data: { targetUserId: user.userId } });
              toast.success("2FA gereset.");
              await router.invalidate();
            },
          },
        ]
      : []),
  ];

  return <DataTableActions forceMenu actions={actions} />;
}

const helper = createDataTableColumnHelper<AdminUser>();

const columns = helper.columns([
  helper.accessor("name", {
    header: () => m["common.fields.name"](),
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {row.original.isBlocked ? (
          <Badge variant="red" size="sm">
            {m["admin.users.table.blocked"]()}
          </Badge>
        ) : null}
        {row.original.name}
      </div>
    ),
  }),
  helper.accessor("email", {
    header: () => m["admin.users.table.email"](),
    enableSorting: true,
  }),
  helper.accessor("scholen", {
    header: () => m["common.labels.scholen"](),
    enableSorting: true,
    cell: ({ getValue }) => {
      const scholen = getValue();
      return scholen.length > 0 ? scholen.join(", ") : "-";
    },
  }),
  helper.accessor("has2FaEnabled", {
    header: () => m["admin.users.table.two_fa"](),
    enableSorting: true,
    cell: ({ getValue }) => (getValue() ? m["common.yes"]() : m["common.no"]()),
  }),
  helper.accessor("createdAt", {
    header: "Toegevoegd op",
    enableSorting: true,
    cell: ({ getValue }) => {
      const createdAt = getValue();
      return createdAt
        ? new Date(createdAt).toLocaleString("nl-NL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-";
    },
  }),
  helper.accessor("lastActivityAt", {
    header: () => m["admin.users.table.last_login"](),
    enableSorting: true,
    cell: ({ getValue }) => {
      const lastActivityAt = getValue();
      return (
        <Badge variant={lastActivityRecencyVariant(lastActivityAt)} size="sm">
          {lastActivityAt
            ? new Date(lastActivityAt).toLocaleString("nl-NL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : m["common.labels.never_logged_in"]()}
        </Badge>
      );
    },
  }),
  helper.display({
    id: "actions",
    meta: { action: true, headerClassName: "w-0" },
    cell: ({ row }) => <UserRowActions user={row.original} />,
  }),
]);

function UsersAdminOverview() {
  const { users, totalPages, totalCount } = Route.useLoaderData();
  const search = Route.useSearch();
  const overview = useOverviewSearch(search, Route.useNavigate());

  const filterBarFilters: FilterBarFilter[] = [
    {
      key: "has2FaEnabled",
      label: m["admin.users.table.two_fa"](),
      options: [
        { value: "true", label: m["common.yes"]() },
        { value: "false", label: m["common.no"]() },
      ],
      selectedValues: search.has2FaEnabled,
      onValuesChange: (values) => overview.setFilter({ has2FaEnabled: values }),
    },
  ];

  return (
    <Page>
      <Page.Header>
        <Page.Header.SearchField>
          <SearchField className={"w-auto grow md:max-w-xs"} {...overview.searchFieldProps} />
        </Page.Header.SearchField>
        <Page.Header.Section>
          <FilterBar
            filters={filterBarFilters}
            onClearAll={() => overview.setFilter({ has2FaEnabled: [] })}
          />
        </Page.Header.Section>
      </Page.Header>
      <PendingContent>
        <DataTable
          alignToContainer
          aria-label={m["admin.users.title"]()}
          columns={columns}
          data={users}
          getRowId={(user) => user.userId}
          {...overview.sortProps}
        />

        <Page.Container>
          <Pager
            totalPages={totalPages}
            totalCount={totalCount}
            className={"mb-6 border-0"}
            {...overview.pagerProps}
          />
        </Page.Container>
      </PendingContent>
    </Page>
  );
}
